import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { NeoCard, NeoBadge } from "@/components/NeoCard";
import { Button } from "@/components/ui/button";
import { formatDayLabel, formatTimeLabel } from "@/lib/availability";
import {
  finalizeMeeting,
  getApiErrorMessage,
  getApiErrorStatus,
  getResults,
  updateMeeting,
} from "@/lib/api";
import { toast } from "sonner";
import {
  CheckCircle,
  Copy,
  Crown,
  EyeOff,
  Globe2,
  Lock,
  RefreshCw,
  Star,
  Trophy,
  Unlock,
  Users,
} from "lucide-react";

export default function Results({ adminMode = false }) {
  const { id } = useParams();
  const [results, setResults] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminToken, setAdminToken] = useState(null);
  const [saving, setSaving] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const load = async () => {
    setLoading(true);
    setErrorStatus(null);
    setErrorMessage("");
    try {
      let token = null;
      if (adminMode) {
        // Try URL fragment first (new format)
        const hash = window.location.hash;
        if (hash && hash.includes("adminToken=")) {
          const match = hash.match(/adminToken=([^&]+)/);
          if (match) {
            token = decodeURIComponent(match[1]);
          }
        }
        // Fallback to query param (deprecated, for backward compatibility)
        if (!token) {
          const params = new URLSearchParams(window.location.search);
          token = params.get("adminToken");
          if (token) {
            console.warn(
              "DEPRECATED: Admin token in query string. Please use the new admin link format with URL fragment."
            );
          }
        }
      }
      setAdminToken(token);
      const data = await getResults(id, token);
      setResults(data);
    } catch (e) {
      setResults(null);
      setErrorStatus(getApiErrorStatus(e));
      setErrorMessage(getApiErrorMessage(e, "Could not load results"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const runAdminAction = async (actionKey, action, successMessage) => {
    setSaving(actionKey);
    try {
      await action();
      toast.success(successMessage);
      await load();
    } catch (e) {
      const message = getApiErrorMessage(e, "Admin action failed");
      setErrorMessage(message);
      toast.error(message);
      console.error(e);
    } finally {
      setSaving(null);
    }
  };

  const copyUrl = async (path, message) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${path}`);
      toast.success(message);
    } catch {
      toast.error("Could not copy link");
    }
  };

  if (loading) {
    return (
      <NeoCard className="max-w-2xl w-full text-center">
        <p className="font-mono text-sm">Crunching numbers...</p>
      </NeoCard>
    );
  }

  if (errorStatus === 401 || errorStatus === 403) {
    return (
      <NeoCard className="max-w-2xl w-full">
        <NeoBadge variant="pink">PRIVATE</NeoBadge>
        <h2 className="neo-heading text-3xl mt-3 mb-2">
          Results need admin access.
        </h2>
        <p className="font-mono text-sm text-black/70 mb-6">
          Ask the organizer for the admin link, or ask them to make results
          public.
        </p>
        {errorMessage && (
          <p className="font-mono text-sm text-[#b00020] mb-4">{errorMessage}</p>
        )}
        <Link
          to={`/m/${id}`}
          className="inline-block bg-black text-white border-[2px] border-black rounded-lg px-4 py-2 shadow-[3px_3px_0_0_#000] font-bold"
        >
          Submit availability
        </Link>
      </NeoCard>
    );
  }

  if (!results) {
    return (
      <NeoCard className="max-w-2xl w-full">
        <NeoBadge variant="pink">NOT FOUND</NeoBadge>
        <h2 className="neo-heading text-3xl mt-3 mb-4">Meeting not found.</h2>
        {errorMessage && (
          <p className="font-mono text-sm text-[#b00020] mb-4">{errorMessage}</p>
        )}
        <Link
          to="/"
          className="inline-block bg-black text-white border-[2px] border-black rounded-lg px-4 py-2 shadow-[3px_3px_0_0_#000] font-bold"
        >
          Create one
        </Link>
      </NeoCard>
    );
  }

  const { meeting, ranked = [], maxCount = 0, responseCount = 0 } = results;
  const isAdmin = Boolean(results.isAdmin && adminToken);
  const participantPath = `/m/${id}`;
  const adminPath = `/admin/${id}#adminToken=${encodeURIComponent(
    adminToken || ""
  )}`;
  const slotsWithResponses = ranked.filter((s) => s.count > 0);
  const bestMatches = slotsWithResponses.filter((s) => s.count === maxCount);
  const preferredOnly = ranked.filter((s) => s.preferred && s.count < maxCount);
  const hasResponses = responseCount > 0;
  const noCommonAvailability =
    hasResponses && responseCount > 1 && maxCount < responseCount;
  const allParticipantsAvailable = hasResponses && maxCount === responseCount;
  const preferredTieBreaker =
    bestMatches.length > 1 &&
    bestMatches[0]?.preferred &&
    bestMatches.some((slot) => !slot.preferred);
  const finalizedSlot = ranked.find((s) => s.id === meeting.finalizedSlot);
  const preferredSlotIds = new Set(meeting.preferredSlots || []);
  const finalLabel = finalizedSlot
    ? `${formatDayLabel(finalizedSlot.day)} at ${formatTimeLabel(finalizedSlot.time)}`
    : meeting.finalizedSlot;

  const setStatus = (status) =>
    runAdminAction(
      `status-${status}`,
      () => updateMeeting(id, { status }, adminToken),
      status === "closed" ? "Meeting closed" : "Meeting reopened"
    );

  const togglePublicResults = () =>
    runAdminAction(
      "public-results",
      () =>
        updateMeeting(
          id,
          { allowResultsPublic: !meeting.allowResultsPublic },
          adminToken
        ),
      meeting.allowResultsPublic ? "Results made private" : "Results made public"
    );

  const togglePreferredSlot = (slotId) => {
    const next = new Set(preferredSlotIds);
    next.has(slotId) ? next.delete(slotId) : next.add(slotId);
    return runAdminAction(
      `preferred-${slotId}`,
      () => updateMeeting(id, { preferredSlots: [...next] }, adminToken),
      next.has(slotId) ? "Preferred slot marked" : "Preferred slot removed"
    );
  };

  const finalizeSlot = (slotId) =>
    runAdminAction(
      `finalize-${slotId}`,
      () => finalizeMeeting(id, slotId, adminToken),
      "Meeting time finalized"
    );

  return (
    <NeoCard className="max-w-3xl w-full" data-testid="results-card">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <NeoBadge variant="lime">
          <Trophy className="w-3 h-3" /> RESULTS
        </NeoBadge>
        <div className="flex items-center gap-2">
          <Button
            onClick={load}
            size="sm"
            variant="outline"
            disabled={loading || Boolean(saving)}
            data-testid="refresh-btn"
            className="bg-white border-[2px] border-black rounded-lg shadow-[2px_2px_0_0_#000] hover:shadow-[3px_3px_0_0_#000] hover:-translate-y-[1px] font-bold"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Link
            to={`/m/${id}`}
            className="font-mono text-xs underline underline-offset-4"
          >
            Submit availability ->
          </Link>
        </div>
      </div>

      <h1 className="neo-heading text-3xl sm:text-4xl leading-[0.95] mb-2">
        {meeting.title}
      </h1>
      <div className="flex items-center gap-2 font-mono text-sm text-black/70 mb-4 flex-wrap">
        <span className="inline-flex items-center gap-1">
          <Users className="w-4 h-4" />
          <span data-testid="response-count">
            {responseCount} {responseCount === 1 ? "response" : "responses"}
          </span>
        </span>
        <span>Times shown in {meeting.timezone || "America/New_York"}</span>
        <NeoBadge variant={meeting.status === "open" ? "cyan" : "pink"}>
          {meeting.status}
        </NeoBadge>
        {meeting.allowResultsPublic && (
          <NeoBadge variant="yellow">PUBLIC RESULTS</NeoBadge>
        )}
      </div>

      {meeting.finalizedSlot && (
        <div className="border-[2px] border-black rounded-xl bg-[#c4f04a] shadow-[4px_4px_0_0_#000] p-4 mb-6">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-5 h-5" />
            <h2 className="neo-heading text-xl">Final meeting time</h2>
          </div>
          <p className="font-mono text-sm text-black/75">{finalLabel}</p>
        </div>
      )}

      {errorMessage && (
        <p className="mb-4 font-mono text-sm text-[#b00020]" role="alert">
          {errorMessage}
        </p>
      )}

      {isAdmin && (
        <section className="border-[2px] border-black rounded-xl bg-[#fff9ed] p-4 mb-8">
          <NeoBadge variant="default">ADMIN</NeoBadge>
          <h2 className="neo-heading text-xl mt-2 mb-3">Organizer controls</h2>
          <div className="grid sm:grid-cols-2 gap-2">
            <Button
              onClick={() =>
                meeting.status === "open" ? setStatus("closed") : setStatus("open")
              }
              disabled={Boolean(saving)}
              className="bg-white text-black hover:bg-[#f7f1dd] border-[2px] border-black rounded-lg shadow-[2px_2px_0_0_#000] font-bold"
            >
              {meeting.status === "open" ? (
                <Lock className="w-4 h-4 mr-2" />
              ) : (
                <Unlock className="w-4 h-4 mr-2" />
              )}
              {saving?.startsWith("status-")
                ? "Saving..."
                : meeting.status === "open"
                ? "Close meeting"
                : "Reopen meeting"}
            </Button>
            <Button
              onClick={togglePublicResults}
              disabled={Boolean(saving)}
              className="bg-white text-black hover:bg-[#f7f1dd] border-[2px] border-black rounded-lg shadow-[2px_2px_0_0_#000] font-bold"
            >
              {meeting.allowResultsPublic ? (
                <EyeOff className="w-4 h-4 mr-2" />
              ) : (
                <Globe2 className="w-4 h-4 mr-2" />
              )}
              {saving === "public-results"
                ? "Saving..."
                : meeting.allowResultsPublic
                ? "Make results private"
                : "Make results public"}
            </Button>
            <Button
              onClick={() => copyUrl(participantPath, "Participant link copied")}
              className="bg-[#ffd60a] hover:bg-[#ffea5c] text-black border-[2px] border-black rounded-lg shadow-[2px_2px_0_0_#000] font-bold"
            >
              <Copy className="w-4 h-4 mr-2" /> Copy participant link
            </Button>
            <Button
              onClick={() => copyUrl(adminPath, "Admin link copied")}
              className="bg-[#ff5e78] hover:bg-[#ffe1e7] text-black border-[2px] border-black rounded-lg shadow-[2px_2px_0_0_#000] font-bold"
            >
              <Copy className="w-4 h-4 mr-2" /> Copy admin link
            </Button>
          </div>
        </section>
      )}

      <section className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <Crown className="w-5 h-5" />
          <h2 className="neo-heading text-xl">Best meeting times</h2>
        </div>
        {!hasResponses ? (
          <div className="border-[2px] border-dashed border-black rounded-xl p-6 text-center font-mono text-sm text-black/60 bg-[#fff9ed]">
            No responses yet. Share the link to get things rolling.
          </div>
        ) : (
          <div className="space-y-3">
            <div className="border-[2px] border-black rounded-xl bg-[#fff9ed] p-3 font-mono text-xs text-black/70">
              {noCommonAvailability
                ? `No common availability yet. Best overlap is ${maxCount}/${responseCount} participants.`
                : allParticipantsAvailable
                ? "All participants are available for the top time."
                : "Best overlap available based on current responses."}
              {preferredTieBreaker && " Preferred slots break the tie between equal overlaps."}
            </div>
            {bestMatches.map((s, i) => (
              <ResultRow
                key={s.id}
                slot={s}
                rank={i + 1}
                highlight
                totalResponses={responseCount}
                isAdmin={isAdmin}
                finalized={meeting.finalizedSlot === s.id}
                saving={saving}
                onFinalize={finalizeSlot}
                onTogglePreferred={togglePreferredSlot}
              />
            ))}
          </div>
        )}
      </section>

      {preferredOnly.length > 0 && (
        <section className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <Star className="w-5 h-5 fill-[#ff5e78]" />
            <h2 className="neo-heading text-xl">Sender preferred</h2>
          </div>
          <div className="space-y-3">
            {preferredOnly.slice(0, 3).map((s) => (
              <ResultRow
                key={s.id}
                slot={s}
                totalResponses={responseCount}
                isAdmin={isAdmin}
                finalized={meeting.finalizedSlot === s.id}
                saving={saving}
                onFinalize={finalizeSlot}
                onTogglePreferred={togglePreferredSlot}
              />
            ))}
          </div>
        </section>
      )}

      {slotsWithResponses.length > bestMatches.length && (
        <section className="mb-2">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-5 h-5" />
            <h2 className="neo-heading text-xl">Most common availability</h2>
          </div>
          <div className="space-y-3">
            {slotsWithResponses
              .filter((s) => s.count < maxCount)
              .slice(0, 6)
              .map((s) => (
                <ResultRow
                  key={s.id}
                  slot={s}
                  totalResponses={responseCount}
                  isAdmin={isAdmin}
                  finalized={meeting.finalizedSlot === s.id}
                  saving={saving}
                  onFinalize={finalizeSlot}
                  onTogglePreferred={togglePreferredSlot}
                />
              ))}
          </div>
        </section>
      )}
    </NeoCard>
  );
}

const ResultRow = ({
  slot,
  rank,
  highlight,
  totalResponses,
  isAdmin,
  finalized,
  saving,
  onFinalize,
  onTogglePreferred,
}) => {
  return (
    <div
      data-testid={`result-row-${slot.id}`}
      className={`relative border-[2px] border-black rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap ${
        highlight
          ? "bg-[#c4f04a] shadow-[4px_4px_0_0_#000]"
          : "bg-[#fff9ed] shadow-[2px_2px_0_0_#000]"
      }`}
    >
      <div className="flex items-center gap-3 min-w-0">
        {rank && (
          <div className="neo-heading w-9 h-9 shrink-0 grid place-items-center border-[2px] border-black rounded-lg bg-black text-white text-sm">
            #{rank}
          </div>
        )}
        <div className="min-w-0">
          <div className="neo-heading text-base sm:text-lg truncate">
            {formatDayLabel(slot.day)}
          </div>
          <div className="font-mono text-sm text-black/70">
            {formatTimeLabel(slot.time)}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap justify-end">
        {finalized && (
          <NeoBadge variant="lime">
            <CheckCircle className="w-3 h-3" /> FINAL
          </NeoBadge>
        )}
        {slot.preferred && (
          <NeoBadge variant="pink">
            <Star className="w-3 h-3 fill-black" /> PREFERRED
          </NeoBadge>
        )}
        {highlight && <NeoBadge variant="default">BEST MATCH</NeoBadge>}
        <div className="border-[2px] border-black rounded-lg px-2.5 py-1 bg-white font-mono text-xs font-bold">
          {slot.count}/{totalResponses} available
        </div>
        {isAdmin && (
          <>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onTogglePreferred(slot.id)}
              disabled={Boolean(saving)}
              className="h-8 bg-white border-[2px] border-black rounded-lg font-bold"
            >
              <Star
                className={`w-3 h-3 mr-1 ${
                  slot.preferred ? "fill-[#ff5e78]" : ""
                }`}
              />
              {saving === `preferred-${slot.id}`
                ? "Saving..."
                : slot.preferred
                ? "Unmark"
                : "Prefer"}
            </Button>
            <Button
              size="sm"
              onClick={() => onFinalize(slot.id)}
              disabled={Boolean(saving) || finalized}
              className="h-8 bg-black text-white hover:bg-black/90 border-[2px] border-black rounded-lg font-bold"
            >
              {saving === `finalize-${slot.id}` ? "Finalizing..." : "Finalize this time"}
            </Button>
          </>
        )}
      </div>
      {slot.names && slot.names.length > 0 && (
        <div className="w-full font-mono text-[11px] text-black/60 truncate">
          {slot.names.join(" / ")}
        </div>
      )}
    </div>
  );
};

// Made with Bob
